import { useState } from 'react'
import { Zap, Loader2, CheckCircle, AlertTriangle } from 'lucide-react'
import { advancedApi } from '../api'
import { useToastStore } from '../store/toast'

interface ChandraScanButtonProps {
  documentId: string
  onScanComplete?: (result: any) => void
  disabled?: boolean
  pageRange?: string
}

export default function ChandraScanButton({ 
  documentId, 
  onScanComplete,
  disabled = false,
  pageRange = '1',
}: ChandraScanButtonProps) {
  const addToast = useToastStore((state) => state.addToast)
  const [scanning, setScanning] = useState(false)
  const [scanResult, setScanResult] = useState<any>(null)

  const handleScan = async () => {
    setScanning(true)
    try {
      const response = await advancedApi.runChandraScan(documentId, pageRange)
      setScanResult(response.data)
      addToast({
        type: 'success',
        message: `Advanced OCR completed: ${response.data.extraction.tables} tables detected.`,
      })
      onScanComplete?.(response.data)
    } catch (err: any) {
      const message = err.response?.data?.error || 'Advanced scan failed.'
      const quotaRemaining = err.response?.data?.advanced_scan_remaining
      
      addToast({
        type: 'error',
        message: quotaRemaining !== undefined 
          ? `${message} Scans remaining: ${quotaRemaining}`
          : message,
      })
    } finally {
      setScanning(false)
    }
  }

  if (scanResult) {
    return (
      <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
        <CheckCircle className="w-4 h-4" />
        <span className="text-sm font-medium">
          Advanced scan complete: {scanResult.extraction.tables} tables on page {pageRange}
        </span>
      </div>
    )
  }

  return (
    <button
      onClick={handleScan}
      disabled={scanning || disabled}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 text-white font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      {scanning ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Running advanced OCR...</span>
        </>
      ) : (
        <>
          <Zap className="w-4 h-4" />
          <span>Advanced Scan</span>
        </>
      )}
    </button>
  )
}

interface ChandraScanInfoProps {
  remaining: number | -1
  limit: number | -1
}

export function ChandraScanInfo({ remaining, limit }: ChandraScanInfoProps) {
  if (remaining === -1) {
    return (
      <div className="flex items-center gap-2 text-sm text-purple-700 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
        <Zap className="w-4 h-4" />
        <span>Unlimited advanced scans available</span>
      </div>
    )
  }

  if (remaining === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
        <AlertTriangle className="w-4 h-4" />
        <span>Advanced scan quota exhausted. Upgrade for unlimited scans.</span>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2 text-sm text-purple-700 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
      <Zap className="w-4 h-4" />
      <span>{remaining} advanced scans remaining this month</span>
      {limit > 0 && (
        <span className="text-purple-500">({limit} monthly quota)</span>
      )}
    </div>
  )
}
